import { FC } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import {
    ProductAttributeItem,
    ProductPomMeasurementSizeGroup,
} from '@/services/query/productPomMeasurement/productPomMeasurement.types';
import './HangerCardSizeInfo.scss';

export interface HangerCardSizeInfoPanelProps {
    barcode: string;
    selectedSizeCode?: string;
    sizeMeasurementText?: string;
    loading: boolean;
    error: string | null;
    fetched: boolean;
    sizes: ProductPomMeasurementSizeGroup[];
    productAttributes: ProductAttributeItem[];
    onFetch: () => void;
    onSizeCodeChange: (sizeCode: string) => void;
    onSizeMeasurementTextChange: (text: string) => void;
    variant?: 'inline' | 'popover';
}

export const getSizeInfoSummary = (
    barcode: string,
    selectedSizeCode?: string,
    sizeMeasurementText?: string,
    sizeInfoLoaded?: boolean,
    sizes: ProductPomMeasurementSizeGroup[] = [],
    loading = false
): string => {
    const trimmedBarcode = barcode?.trim() ?? '';

    if (!trimmedBarcode || loading || !sizeInfoLoaded) {
        return '';
    }

    const hasSizeData = sizes.length > 0;
    const hasMeasurementText = Boolean(sizeMeasurementText?.trim());

    if (!hasSizeData && !hasMeasurementText) {
        return '';
    }

    if (hasSizeData && selectedSizeCode?.trim()) {
        return selectedSizeCode.trim();
    }

    if (hasMeasurementText) {
        return 'ready';
    }

    return '';
};

export const HangerCardSizeInfoPanel: FC<HangerCardSizeInfoPanelProps> = ({
    barcode,
    selectedSizeCode = '',
    sizeMeasurementText = '',
    loading,
    error,
    fetched,
    sizes,
    productAttributes,
    onFetch,
    onSizeCodeChange,
    onSizeMeasurementTextChange,
    variant = 'inline',
}) => {
    const { t } = useTranslation();
    const trimmedBarcode = barcode?.trim() ?? '';

    const hasSizeData = sizes.length > 0;
    const hasPersistedData =
        Boolean(selectedSizeCode?.trim()) || Boolean(sizeMeasurementText?.trim());
    const hasAnyData = hasSizeData || productAttributes.length > 0 || hasPersistedData;
    const activeSizeCode = selectedSizeCode || sizes[0]?.sizeCode || '';

    return (
        <Box className={`hanger-size-info-panel ${variant === 'popover' ? 'hanger-size-info-panel--popover' : ''}`}>
            {!trimmedBarcode && (
                <Box className="hanger-size-info-panel__hint-card">
                    <Typography variant="body2" className="hanger-size-info-panel__hint">
                        {t('mannequin.aiGeneration.configuration.sizeInfo.noBarcode')}
                    </Typography>
                </Box>
            )}

            {trimmedBarcode && (
                <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={loading}
                    onClick={onFetch}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome fontSize="small" />}
                    className="hanger-size-info-panel__fetch-btn"
                >
                    {loading
                        ? t('mannequin.aiGeneration.configuration.sizeInfo.loading')
                        : t('mannequin.aiGeneration.configuration.sizeInfo.fetch')}
                </Button>
            )}

            {trimmedBarcode && !loading && error && (
                <Typography variant="body2" className="hanger-size-info-panel__message hanger-size-info-panel__message--error">
                    {error}
                </Typography>
            )}

            {trimmedBarcode && !loading && !error && fetched && !hasAnyData && (
                <Typography variant="body2" className="hanger-size-info-panel__message">
                    {t('mannequin.aiGeneration.configuration.sizeInfo.empty')}
                </Typography>
            )}

            {trimmedBarcode && !loading && !error && hasAnyData && (
                <Box className="hanger-size-info-panel__fields">
                    {hasSizeData && (
                        <Box className="hanger-size-info-panel__section">
                            <Typography variant="caption" className="hanger-size-info-panel__label">
                                {t('mannequin.aiGeneration.configuration.sizeInfo.sizeCode')}
                            </Typography>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={activeSizeCode}
                                onChange={(_, value) => {
                                    if (value) {
                                        onSizeCodeChange(value);
                                    }
                                }}
                                className="hanger-size-info-panel__segment"
                                aria-label={t('mannequin.aiGeneration.configuration.sizeInfo.sizeCode')}
                            >
                                {sizes.map((size) => (
                                    <ToggleButton
                                        key={size.sizeCode}
                                        value={size.sizeCode}
                                        className="hanger-size-info-panel__segment-btn"
                                    >
                                        {size.sizeCode}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    {productAttributes.length > 0 && (
                        <Box className="hanger-size-info-panel__attributes">
                            {productAttributes.map((attribute) => (
                                <Chip
                                    key={`${attribute.key}-${attribute.value}`}
                                    size="small"
                                    label={`${attribute.key}: ${attribute.value}`}
                                    className="hanger-size-info-panel__attribute-chip"
                                />
                            ))}
                        </Box>
                    )}

                    <Box className="hanger-size-info-panel__textarea-wrap">
                        <Typography variant="caption" component="label" className="hanger-size-info-panel__label">
                            {t('mannequin.aiGeneration.configuration.sizeInfo.measurements')}
                        </Typography>
                        <textarea
                            className="hanger-size-info-panel__textarea"
                            rows={5}
                            value={sizeMeasurementText}
                            onChange={(e) => onSizeMeasurementTextChange(e.target.value)}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};
